import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Search, Plus, Filter, RefreshCw,
  MoreVertical, Trash2, PauseCircle, PlayCircle, X,
  Mail, Phone, Shield, Clock, FileText, CheckCircle,
  AlertCircle, Eye, ChevronRight, CheckCircle2, XCircle
} from 'lucide-react';
import api from '../../services/apiService';
import { toast } from 'react-toastify';
import OEMAccessDenied from './OEMAccessDenied';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const OEMCompaniesManagement = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectCompanies, setProjectCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);

  // Filters
  const [selectedProject, setSelectedProject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Selected company for details panel
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingToProject, setAddingToProject] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyToAdd, setSelectedCompanyToAdd] = useState(null);
  const [addingCompany, setAddingCompany] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  // Technicians data
  const [techniciansData, setTechniciansData] = useState(null);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [technicianSearch, setTechnicianSearch] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);

  // Action menu
  const [activeMenu, setActiveMenu] = useState(null);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/oem/projects/');
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchProjectCompanies = async () => {
    try {
      const params = selectedProject !== 'all' ? `?project=${selectedProject}` : '';
      const response = await api.get(`/oem/project-companies/${params}`);
      setProjectCompanies(response.data);
    } catch (err) {
      console.error('Error fetching project companies:', err);
    }
  };

  const fetchAvailableCompanies = async (projectId) => {
    setLoadingAvailable(true);
    try {
      const response = await api.get(`/oem/available-companies/?project=${projectId}`);
      setAvailableCompanies(response.data);
    } catch (err) {
      console.error('Error fetching available companies:', err);
      toast.error('Failed to load available companies');
    } finally {
      setLoadingAvailable(false);
    }
  };

  const fetchCompanyTechnicians = async (projectCompanyId) => {
    setLoadingTechnicians(true);
    setTechniciansData(null);
    setSelectedTechnician(null);
    setPreviewDocument(null);
    try {
      const response = await api.get(`/oem/project-companies/${projectCompanyId}/technicians/`);
      setTechniciansData(response.data);
    } catch (err) {
      console.error('Error fetching technicians:', err);
      toast.error('Failed to load technicians');
    } finally {
      setLoadingTechnicians(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchProjects(), fetchProjectCompanies()]);
    setLoading(false);
  };

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await api.get('/oem/dashboard/team/');
        const roles = response.data.my_roles || [];
        const hasMasterRole = roles.some(r => r.role === 'master');
        const hasOnlyRestrictedRoles = roles.length > 0 && !hasMasterRole;
        if (hasOnlyRestrictedRoles) {
          setAccessDenied(true);
        }
      } catch (err) {
        // If endpoint fails, user has no team roles - allow access
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (!accessDenied) {
      loadData();
    }
  }, [accessDenied]);

  useEffect(() => {
    if (!accessDenied) {
      fetchProjectCompanies();
    }
  }, [selectedProject, accessDenied]);

  // Load technicians when company is selected
  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyTechnicians(selectedCompany.id);
    }
  }, [selectedCompany]);

  // Filter companies
  const filteredCompanies = projectCompanies.filter(pc => {
    if (filterStatus !== 'all' && pc.status !== filterStatus) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return pc.company_name?.toLowerCase().includes(search) ||
             pc.company_address?.toLowerCase().includes(search);
    }
    return true;
  });

  // Filter available companies
  const filteredAvailableCompanies = availableCompanies.filter(c => {
    if (!companySearch) return true;
    const search = companySearch.toLowerCase();
    return c.name?.toLowerCase().includes(search) ||
           c.address?.toLowerCase().includes(search);
  });

  // Filter technicians
  const filteredTechnicians = techniciansData?.technicians?.filter(tech => {
    if (!technicianSearch) return true;
    const search = technicianSearch.toLowerCase();
    return tech.full_name?.toLowerCase().includes(search) ||
           tech.email?.toLowerCase().includes(search);
  }) || [];

  const openAddModal = (projectId) => {
    setAddingToProject(projectId);
    setShowAddModal(true);
    setCompanySearch('');
    setSelectedCompanyToAdd(null);
    fetchAvailableCompanies(projectId);
  };

  const handleAddCompany = async () => {
    if (!selectedCompanyToAdd || !addingToProject) return;
    setAddingCompany(true);
    try {
      await api.post(`/oem/projects/${addingToProject}/companies/`, {
        company: selectedCompanyToAdd.id
      });
      toast.success('Company added successfully');
      setShowAddModal(false);
      setSelectedCompanyToAdd(null);
      fetchProjectCompanies();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add company');
    } finally {
      setAddingCompany(false);
    }
  };

  const handleStatusChange = async (companyId, newStatus) => {
    try {
      await api.patch(`/oem/project-companies/${companyId}/`, { status: newStatus });
      toast.success(`Company ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      fetchProjectCompanies();
      if (selectedCompany?.id === companyId) {
        setSelectedCompany(prev => ({ ...prev, status: newStatus }));
      }
      setActiveMenu(null);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleRemoveCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to remove this company?')) return;
    try {
      await api.delete(`/oem/project-companies/${companyId}/`);
      toast.success('Company removed');
      if (selectedCompany?.id === companyId) {
        setSelectedCompany(null);
        setTechniciansData(null);
      }
      fetchProjectCompanies();
      setActiveMenu(null);
    } catch (err) {
      toast.error('Failed to remove company');
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? `${project.code}` : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Stats
  const stats = {
    total: projectCompanies.length,
    active: projectCompanies.filter(pc => pc.status === 'active').length,
    suspended: projectCompanies.filter(pc => pc.status === 'suspended').length,
    unique: new Set(projectCompanies.map(pc => pc.company)).size,
  };

  if (accessDenied) {
    return <OEMAccessDenied message="You don't have permission to manage Companies." />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 font-medium">Loading companies...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="text-slate-900">
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header Row - Stats & Actions */}
        <div className="flex items-center justify-between gap-4 mb-6">
          {/* Stats - Inline */}
          <div className="flex items-center gap-2">
            <div className="h-10 flex items-center gap-2 px-4 bg-white rounded-xl border border-slate-200">
              <Building2 size={16} className="text-slate-400" />
              <span className="text-sm font-black text-slate-900">{stats.total}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">Total</span>
            </div>
            <div className="h-10 flex items-center gap-2 px-4 bg-white rounded-xl border border-slate-200">
              <CheckCircle size={16} className="text-emerald-500" />
              <span className="text-sm font-black text-emerald-600">{stats.active}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">Active</span>
            </div>
            <div className="h-10 flex items-center gap-2 px-4 bg-white rounded-xl border border-slate-200">
              <PauseCircle size={16} className="text-amber-500" />
              <span className="text-sm font-black text-amber-600">{stats.suspended}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">Suspended</span>
            </div>
            <div className="h-10 flex items-center gap-2 px-4 bg-white rounded-xl border border-slate-200">
              <Users size={16} className="text-blue-500" />
              <span className="text-sm font-black text-blue-600">{stats.unique}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">Unique</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-500 hover:text-orange-500 hover:border-orange-300 transition-all shadow-sm"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            {projects.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => projects.length === 1 ? openAddModal(projects[0].id) : document.getElementById('project-dropdown')?.classList.toggle('hidden')}
                  className="h-10 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-black text-[11px] uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-orange-500/20"
                >
                  <Plus className="h-4 w-4" />
                  Add Company
                </button>
                {projects.length > 1 && (
                  <div id="project-dropdown" className="hidden absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-xl z-20 overflow-hidden">
                    <div className="p-3 text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Select Project</div>
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => {
                          document.getElementById('project-dropdown')?.classList.add('hidden');
                          openAddModal(project.id);
                        }}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-orange-50 flex items-center gap-3 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-orange-500" />
                        </div>
                        <span className="truncate font-black text-slate-700">{project.code} - {project.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search companies..."
                className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm transition-all"
              />
            </div>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="h-10 pl-3 pr-8 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm font-medium"
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.code} - {project.name}</option>
              ))}
            </select>
            <div className="flex bg-slate-100 p-1 rounded-xl">
              {[
                { key: 'all', label: 'All' },
                { key: 'active', label: 'Active' },
                { key: 'suspended', label: 'Suspended' },
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setFilterStatus(filter.key)}
                  className={`px-3 py-1.5 text-[10px] font-black rounded-lg uppercase transition-all ${
                    filterStatus === filter.key
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Companies List */}
          <div className="lg:col-span-1">
            <h3 className="text-[10px] font-black tracking-[0.2em] uppercase text-orange-500 px-1 mb-3">
              Project Companies
            </h3>
            <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
              {filteredCompanies.length === 0 ? (
                <div className="p-8 text-center bg-white rounded-2xl border border-slate-200">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Building2 size={20} className="text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-xs font-medium">No companies found</p>
                </div>
              ) : (
                filteredCompanies.map(pc => (
                  <button
                    key={pc.id}
                    onClick={() => setSelectedCompany(pc)}
                    className={`w-full p-4 rounded-2xl border transition-all text-left flex items-center gap-4 ${
                      selectedCompany?.id === pc.id
                        ? 'bg-orange-500 border-orange-400 text-white scale-[1.01] shadow-lg shadow-orange-500/20'
                        : 'bg-white border-slate-200 hover:border-orange-300 text-slate-900 shadow-sm'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${
                      selectedCompany?.id === pc.id
                        ? 'bg-white/20 text-white'
                        : 'bg-orange-100 text-orange-600'
                    }`}>
                      <Building2 size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block font-black text-sm leading-tight truncate">
                        {pc.company_name}
                      </span>
                      <span className={`block text-[9px] font-bold uppercase tracking-widest mt-0.5 ${
                        selectedCompany?.id === pc.id ? 'text-white/70' : 'text-orange-500'
                      }`}>
                        {getProjectName(pc.project)} • {pc.technicians_count || 0} technicians
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        pc.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'
                      } ${selectedCompany?.id === pc.id ? '' : ''}`} />
                      <ChevronRight size={14} className={selectedCompany?.id === pc.id ? 'text-white/70' : 'text-slate-400'} />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right - Company Details */}
          <div className="lg:col-span-2 pt-5">
            {selectedCompany ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 relative shadow-sm">
                {/* Company Header - Compact */}
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-14 h-14 rounded-xl border-2 border-orange-500 bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Building2 size={24} className="text-orange-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-lg font-black text-slate-900 tracking-tight truncate">
                        {selectedCompany.company_name}
                      </h2>
                      <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase flex-shrink-0 ${
                        selectedCompany.status === 'active'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-amber-100 text-amber-600'
                      }`}>
                        {selectedCompany.status}
                      </span>
                    </div>
                    <p className="text-orange-500 font-black text-[9px] tracking-[0.15em] uppercase">
                      Project {getProjectName(selectedCompany.project)}
                    </p>
                  </div>

                  {/* Stats inline */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center px-3">
                      <span className="block text-lg font-black text-slate-900">{selectedCompany.technicians_count || 0}</span>
                      <span className="block text-[8px] text-slate-400 uppercase font-bold">Technicians</span>
                    </div>
                    <div className="text-center px-3 border-l border-slate-200">
                      <span className="block text-lg font-black text-emerald-600">{selectedCompany.authorized_count || 0}</span>
                      <span className="block text-[8px] text-slate-400 uppercase font-bold">Authorized</span>
                    </div>
                    <div className="text-center px-3 border-l border-slate-200">
                      <span className="block text-xs font-black text-slate-700">{formatDate(selectedCompany.added_at)}</span>
                      <span className="block text-[8px] text-slate-400 uppercase font-bold">Added</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={() => setActiveMenu(activeMenu === selectedCompany.id ? null : selectedCompany.id)}
                      className="p-2 bg-slate-100 rounded-lg border border-slate-200 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-all"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {activeMenu === selectedCompany.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                        {selectedCompany.status === 'active' ? (
                          <button
                            onClick={() => handleStatusChange(selectedCompany.id, 'suspended')}
                            className="w-full px-4 py-2.5 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2 font-medium"
                          >
                            <PauseCircle className="h-4 w-4" />
                            Suspend Company
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(selectedCompany.id, 'active')}
                            className="w-full px-4 py-2.5 text-left text-sm text-emerald-600 hover:bg-emerald-50 flex items-center gap-2 font-medium"
                          >
                            <PlayCircle className="h-4 w-4" />
                            Activate Company
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveCompany(selectedCompany.id)}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove Company
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center bg-white border border-dashed border-slate-300 rounded-3xl text-slate-500 p-16 text-center group min-h-[400px]">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 border border-slate-200 group-hover:border-orange-300 transition-all">
                  <Building2 size={24} className="text-orange-500/40" />
                </div>
                <h3 className="text-sm font-black text-slate-500 uppercase tracking-tighter">No Company Selected</h3>
                <p className="text-[10px] max-w-xs mt-2 font-medium text-slate-400 leading-relaxed">
                  Select a company from the list to view details and manage technicians.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Technicians Table - Full Width */}
        {selectedCompany && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] font-black flex items-center gap-3 text-slate-900 uppercase tracking-widest">
                <Users size={16} className="text-orange-500" />
                Company Technicians
                <span className="text-slate-400">— {selectedCompany.company_name}</span>
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={technicianSearch}
                  onChange={(e) => setTechnicianSearch(e.target.value)}
                  placeholder="Search technicians..."
                  className="h-10 pl-10 pr-4 w-64 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                />
              </div>
            </div>

            {loadingTechnicians ? (
              <div className="p-12 text-center bg-white rounded-2xl border border-slate-200">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-slate-500 mt-3 text-sm">Loading technicians...</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Fixed Header */}
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-5 py-4 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest w-[30%]">Technician</th>
                      <th className="px-5 py-4 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest w-[18%]">Authorization</th>
                      <th className="px-5 py-4 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest w-[15%]">Valid Until</th>
                      <th className="px-5 py-4 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest w-[17%]">Documents</th>
                      <th className="px-5 py-4 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest w-[20%]">Status</th>
                    </tr>
                  </thead>
                </table>
                {/* Scrollable Body */}
                <div className="max-h-[500px] overflow-y-auto">
                  <table className="w-full">
                    <tbody className="divide-y divide-slate-100">
                      {filteredTechnicians.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-5 py-16 text-center">
                            <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-slate-500">No technicians found</p>
                            <p className="text-xs text-slate-400 mt-1">This company has no technicians assigned</p>
                          </td>
                        </tr>
                      ) : (
                        filteredTechnicians.map(tech => (
                          <tr key={tech.id} className="hover:bg-slate-50 transition-colors">
                            {/* Technician */}
                            <td className="px-5 py-4 w-[30%]">
                              <div className="flex items-center gap-3">
                                {tech.profile_picture ? (
                                  <img
                                    src={getImageUrl(tech.profile_picture)}
                                    alt={tech.full_name}
                                    className="w-10 h-10 rounded-lg object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                                    <span className="text-sm font-black text-orange-600">
                                      {tech.first_name?.charAt(0)?.toUpperCase() || tech.full_name?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-bold text-slate-900">{tech.full_name}</p>
                                  <p className="text-xs text-slate-500">{tech.email}</p>
                                </div>
                              </div>
                            </td>

                            {/* Authorization */}
                            <td className="px-5 py-4 w-[18%]">
                              {tech.authorization ? (
                                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-black uppercase">
                                  {tech.authorization.level_code}
                                </span>
                              ) : tech.pending_nomination ? (
                                <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-lg text-[10px] font-black uppercase">
                                  Pending {tech.pending_nomination.authorization_level_code}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>

                            {/* Valid Until */}
                            <td className="px-5 py-4 w-[15%]">
                              {tech.authorization ? (
                                <span className={`text-sm font-medium ${tech.authorization.is_expired ? 'text-red-600' : 'text-slate-700'}`}>
                                  {formatDate(tech.authorization.valid_until)}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>

                            {/* Documents */}
                            <td className="px-5 py-4 w-[17%]">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-slate-700">
                                  {tech.qualifications?.length || 0}
                                </span>
                                {tech.qualifications?.some(q => q.is_expired) && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-lg text-[9px] font-bold">
                                    {tech.qualifications.filter(q => q.is_expired).length} expired
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4 w-[20%]">
                              {tech.authorization ? (
                                tech.authorization.is_expired ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase bg-red-50 text-red-700">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                    Expired
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    Active
                                  </span>
                                )
                              ) : tech.pending_nomination ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase bg-amber-50 text-amber-700">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                  Pending
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase bg-slate-100 text-slate-500">
                                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                  None
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Footer with count */}
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    Showing <span className="font-bold text-slate-700">{filteredTechnicians.length}</span> of <span className="font-bold text-slate-700">{techniciansData?.technicians?.length || 0}</span> technicians
                  </span>
                  {filteredTechnicians.length > 10 && (
                    <span className="text-[10px] text-slate-400">Scroll to see more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Company Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowAddModal(false)}>
            <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Add Company</h3>
                  <p className="text-[10px] text-orange-500 font-bold tracking-widest uppercase">
                    Project: {projects.find(p => p.id === addingToProject)?.code}
                  </p>
                </div>
                <button onClick={() => setShowAddModal(false)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>

              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    placeholder="Search companies..."
                    className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                  />
                </div>
              </div>

              <div className="overflow-y-auto max-h-[400px]">
                {loadingAvailable ? (
                  <div className="p-8 text-center">
                    <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-slate-500 mt-2">Loading...</p>
                  </div>
                ) : filteredAvailableCompanies.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    {companySearch ? 'No companies found' : 'No available companies'}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredAvailableCompanies.map((company) => (
                      <div
                        key={company.id}
                        onClick={() => setSelectedCompanyToAdd(company)}
                        className={`p-4 cursor-pointer hover:bg-orange-50 transition-colors ${
                          selectedCompanyToAdd?.id === company.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-orange-500" />
                            </div>
                            <div>
                              <h4 className="font-black text-slate-900">{company.name}</h4>
                              <p className="text-sm text-slate-500">{company.address}</p>
                            </div>
                          </div>
                          {selectedCompanyToAdd?.id === company.id && (
                            <CheckCircle className="h-5 w-5 text-orange-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="h-9 px-4 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 font-black text-[10px] uppercase tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCompany}
                  disabled={!selectedCompanyToAdd || addingCompany}
                  className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 font-black text-[10px] uppercase tracking-wider transition-all shadow-lg shadow-orange-500/20"
                >
                  {addingCompany ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Add Company
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Click outside to close menu */}
        {activeMenu && (
          <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)} />
        )}
      </div>
    </div>
  );
};

export default OEMCompaniesManagement;
